/**
 * ===========================================================================
 *  COMMAND — the loop that keeps the war going
 * ===========================================================================
 *
 * `squad.js` knows how to walk a formation somewhere and how to fight what it
 * bumps into. What it never had was **somewhere else to go afterwards**: an
 * ADVANCE cancelled itself the moment the anchor reached its one spawn-time
 * goal and an ENGAGE reverted to HOLD when the fight went quiet, so every
 * squad on the map ran out of work inside the first fifteen seconds and the
 * battlefield froze into a set of static ranks. Measured on the broken build:
 * 149 of 498 live men had a target at all, 6 % were mid-swing, and the two
 * knots at the Brantas crossing stood 35 m apart on opposite banks for the whole
 * capture with a bridge between them.
 *
 * This module is the general. Every fixed step it looks at a **slice** of the
 * squad registry — never all of it, the peak frame budget is 9 ms for the
 * whole game — and hands a new objective to any squad that has run out of one:
 *
 *   1. an officer's retinue escorts him,
 *   2. a garrison holds its base (and patrols it when nothing threatens),
 *   3. everyone else marches at the nearest enemy squad, biased away from
 *      squads their neighbours have already claimed so the army spreads along
 *      a front instead of collapsing into one column,
 *   4. and if there is no enemy squad in reach, at the nearest site the enemy
 *      still holds.
 *
 * Costs O(squads) per assignment and assigns at most `SLICE` squads per step.
 */
import { TEAM, ST } from '../core/entities.js';
import { ORDERS, FORM } from './store.js';
import { SITES } from '../world/layout.js';

/**
 * `Math.hypot` is NOT a cheap intrinsic in V8: it is a variadic builtin that
 * boxes its arguments, and it measured at ~155 bytes of garbage per call
 * against ~0 for `Math.sqrt`. This module calls it once or more per unit per
 * fixed step, which is the whole of the AI's allocation rate and therefore the
 * whole of `aiCpuPeak` — the mean was never the problem, the scavenges landing
 * inside the unit loop were. Same arithmetic, no allocation.
 */
const hyp = (x, z) => Math.sqrt(x * x + z * z);


/** What a squad was last told to do. Recorded for debugging and for the HUD. */
export const OBJ = { NONE: 0, ATTACK: 1, ESCORT: 2, GARRISON: 3, PATROL: 4, PUSH: 5, RALLY: 6 };
export const OBJ_NAME = ['none', 'attack', 'escort', 'garrison', 'patrol', 'push', 'rally'];

/** How many squads may be handed a new objective in one fixed step. */
const SLICE = 2;
/** How far a squad will look for an enemy squad to march at. */
const RANGE = 240;
/** Extra "distance" charged for every friendly squad already sent at a target. */
const CLAIM_COST = 50;
/** A garrison will not chase further than this from the base it holds. */
const GARRISON_LEASH = 135;

export function createCommander() {
  let cursor = 0;
  const claims = new Int16Array(256);
  let patrolK = 0;

  /** Nearest site to (x,z) whose team is `team`. Returns index or -1. */
  function nearestSite(x, z, team, maxD) {
    let best = -1, bd = maxD * maxD;
    for (let k = 0; k < SITES.length; k++) {
      const s = SITES[k];
      if (team >= 0 && s.team !== team) continue;
      const dx = s.x - x, dz = s.z - z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bd) { bd = d2; best = k; }
    }
    return best;
  }

  /** Shape and pace for a march of length `d`. */
  function gait(S, i, d) {
    if (d > 72) { S.form[i] = FORM.COLUMN; S.speed[i] = 3.9; S.spacing[i] = 1.9; S.road[i] = 1; }
    else if (d > 24) { S.form[i] = FORM.WEDGE; S.speed[i] = 4.2; S.spacing[i] = 1.85; S.road[i] = 0; }
    else { S.form[i] = FORM.WEDGE; S.speed[i] = 4.5; S.spacing[i] = 1.75; S.road[i] = 0; }
  }

  /**
   * Give squad `i` something to do. Returns true when an order was issued.
   */
  function assign(ctx, i) {
    const S = ctx.squads, w = ctx.w, e = w.entities;
    const team = S.team[i];
    const foeTeam = team === TEAM.PLAYER ? TEAM.ENEMY : TEAM.PLAYER;
    const ax = S.ax[i], az = S.az[i];

    // ---- is this a base garrison? ------------------------------------
    // `baseId` is written from a string site id, so it is useless; the honest
    // test is simply "is this squad standing in one of its own bases".
    const homeSite = nearestSite(ax, az, team, 46);
    const leash = homeSite >= 0 ? GARRISON_LEASH : RANGE;

    // ---- 1. the nearest enemy squad ----------------------------------
    let best = -1, bs = 1e18, bd = 0;
    for (let j = 0; j < S.cap; j++) {
      if (!S.alive[j] || S.count[j] <= 0 || S.team[j] === team) continue;
      if (S.order[j] === ORDERS.ROUT) continue;            // let them run
      const dx = S.ax[j] - ax, dz = S.az[j] - az;
      const d = hyp(dx, dz);
      if (d > leash) continue;
      const score = d + claims[j] * CLAIM_COST;
      if (score < bs) { bs = score; best = j; bd = d; }
    }

    if (best >= 0) {
      // Send the other side to meet him. Two squads walking at each other halve
      // the time the map spends in transit instead of in contact, and it is
      // also the only thing that produces a real FRONT — one squad walking into
      // a stationary one just gets absorbed.
      //
      // They must be given the SAME meeting point, though. Aiming each of them
      // at where the other one currently stands makes two moving squads swap
      // positions and never touch — which is exactly what the two knots at the
      // Brantas crossing did once they were finally ordered to move.
      const mutual = S.order[best] === ORDERS.HOLD && S.since[best] > 0.4 && S.escort[best] < 0;
      march(ctx, i, best, bd, mutual);
      if (mutual) march(ctx, best, i, bd, true);
      return true;
    }

    // ---- 2. a garrison with nothing to fight patrols its base ---------
    // Not all of them: a fort whose every squad stands on the wall for the
    // whole battle is exactly the frozen map this module exists to fix. One in
    // three holds the gate, the rest go and find the war (and the director's
    // refill puts a fresh garrison behind them).
    if (homeSite >= 0 && (i % 3) === 0) {
      const s = SITES[homeSite];
      const a = (patrolK++ * 1.7) + i * 0.37;
      const r = s.radius * 0.62;
      S.form[i] = FORM.LINE; S.speed[i] = 1.9; S.road[i] = 0;
      S.obj[i] = OBJ.PATROL; S.objSq[i] = -1;
      S.setOrder(i, ORDERS.ADVANCE, s.x + Math.sin(a) * r, s.z + Math.cos(a) * r);
      return true;
    }

    // ---- 3. nothing near: push at a site the enemy holds --------------
    let site = nearestSite(ax, az, foeTeam, 999);
    if (site < 0) site = nearestSite(ax, az, TEAM.NEUTRAL, 999);
    if (site >= 0) {
      const s = SITES[site];
      const dx = s.x - ax, dz = s.z - az;
      const d = hyp(dx, dz) || 1;
      if (d > 6) {
        const stop = Math.max(0, d - s.radius * 0.8);
        gait(S, i, d);
        S.obj[i] = OBJ.PUSH; S.objSq[i] = -1;
        S.setOrder(i, ORDERS.ADVANCE, ax + (dx / d) * stop, az + (dz / d) * stop);
        return true;
      }
    }

    // ---- 4. genuinely nothing to do: sit for a while ------------------
    S.since[i] = -3.5;
    return false;
  }

  /** Order squad `i` to march on squad `t`, `d` metres away. */
  function march(ctx, i, t, d, mutual) {
    const S = ctx.squads, e = ctx.w.entities;
    const ux = (S.ax[t] - S.ax[i]) / (d || 1), uz = (S.az[t] - S.az[i]) / (d || 1);
    let gx, gz;
    if (mutual) {
      // both are walking: meet in the middle, a few metres apart, so the two
      // formations arrive facing each other instead of interpenetrating
      gx = (S.ax[i] + S.ax[t]) * 0.5 - ux * 3.5;
      gz = (S.az[i] + S.az[t]) * 0.5 - uz * 3.5;
    } else {
      // stand off a few metres: two anchors meeting exactly on top of one
      // another is what produced interleaved red and blue with no front.
      const stand = Math.min(7.5, d * 0.4);
      gx = S.ax[t] - ux * stand; gz = S.az[t] - uz * stand;
    }
    gait(S, i, d);
    S.obj[i] = OBJ.ATTACK; S.objSq[i] = t;
    claims[t]++;
    S.setOrder(i, S.escort[i] >= 0 && escortAlive(e, S.escort[i]) ? ORDERS.ESCORT : ORDERS.ADVANCE, gx, gz);
  }

  function escortAlive(e, id) { return id >= 0 && e.alive[id] && e.state[id] !== ST.DEAD; }

  return {
    OBJ,
    /** Restore deterministic round-robin and patrol sequencing on retry. */
    resetBattle() {
      cursor = 0;
      patrolK = 0;
      claims.fill(0);
    },
    /** One slice of re-tasking. Called from ai.fixedUpdate. */
    tick(ctx, dt) {
      const S = ctx.squads, w = ctx.w, e = w.entities;

      // Who is already being marched at? Rebuilt every step (O(squads)); a
      // stale claim would quietly funnel the whole army at one knot.
      claims.fill(0, 0, S.cap);
      for (let j = 0; j < S.cap; j++) {
        if (!S.alive[j]) continue;
        const t = S.objSq[j];
        if (t >= 0 && t < S.cap && S.alive[t]) claims[t]++;
      }

      /* A PLAYER ORDER OUTRANKS THIS MODULE (DEV_PLAN Step 5).
       *
       * `world.tactics` stamps `playerT` when the player gives an order, and
       * until it runs out this loop leaves that squad alone. Without it the
       * retask below would take the squad back within a second of its order
       * settling — the `S.since[i] < 1.0` gate is the whole hold a HOLD gets —
       * and the player's input would be obeyed for three paces and then
       * silently undone, which is indistinguishable from not being wired up.
       *
       * Decayed for EVERY squad each tick, not just the sliced ones: this loop
       * visits SLICE squads a step, so a per-visit decay would run a squad's
       * hold down at a rate that depended on how many squads were alive. */
      for (let j = 0; j < S.cap; j++) {
        if (S.playerT[j] > 0) {
          S.playerT[j] -= dt;
          if (S.playerT[j] < 0) S.playerT[j] = 0;
        }
      }

      let done = 0;
      for (let k = 0; k < S.cap && done < SLICE; k++) {
        cursor = (cursor + 1) % S.cap;
        const i = cursor;
        if (!S.alive[i] || S.count[i] <= 0) continue;
        if (S.playerT[i] > 0) continue;              // his orders, not ours
        const ord = S.order[i];

        if (ord === ORDERS.ADVANCE) {
          // the squad it was sent to kill is gone — find another one
          const t = S.objSq[i];
          if (t >= 0 && (!S.alive[t] || S.count[t] <= 0)) { S.objSq[i] = -1; S.since[i] = 99; S.order[i] = ORDERS.HOLD; }
          else if (t >= 0 && S.since[i] > 3.5) {
            // the squad it was sent at has moved on; a stale goal is a march to
            // an empty field
            const dx = S.gx[i] - S.ax[t], dz = S.gz[i] - S.az[t];
            if (dx * dx + dz * dz > 900) { S.since[i] = 99; S.order[i] = ORDERS.HOLD; }
            else continue;
          } else continue;
        } else if (ord === ORDERS.ESCORT) {
          // an escort has arrived when its officer has
          const id = S.escort[i];
          if (!escortAlive(e, id)) { S.order[i] = ORDERS.HOLD; S.since[i] = 99; }
          else {
            const dx = S.gx[i] - e.x[id], dz = S.gz[i] - e.z[id];
            if (dx * dx + dz * dz > 64 && S.since[i] < 26) continue;
          }
        } else if (ord !== ORDERS.HOLD) continue;      // ENGAGE / ROUT own themselves

        if (S.since[i] < 1.0) continue;
        if (assign(ctx, i)) done++;
      }
      ctx.stats.retasked += done;
    },
  };
}
